import type { ReactNode } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import type { Questionnaire } from "@/generated/core/v1/questionnaire";
import { financingCost, mortgagePayment } from "@/plan-logic/lib/amortization";
import { money } from "@/plan-logic/lib/format";
import { fuelMonthly } from "@/plan-logic/decisions/changes";
import {
  carDefaults,
  carRun,
  carSchema,
  type CarValues,
  expenseDefaults,
  expenseRun,
  expenseSchema,
  type ExpenseValues,
  financedDefaults,
  financedRun,
  financedSchema,
  type FinancedValues,
  homeDefaults,
  homeRun,
  homeSchema,
  type HomeValues,
  LENDERS,
  LOAN_TYPES,
} from "@/plan-logic/decisions/values";
import { usePlanCtx } from "../PlanPage";
import { amount, DateInput, Field, MoneyInput, RunButton, TextInput, useEdits } from "./fields";

export type ToolFormProps = {
  /**
   * `comparison`: a second set of changes the same job runs alongside (insurance: the event on the
   * coverage held now).
   */
  onRun: (
    changes: Questionnaire,
    inputs: Record<string, unknown>,
    comparison?: Questionnaire,
  ) => void;
  busy: boolean;
  /** The form values of an opened scenario. */
  defaults?: Record<string, unknown>;
  /** Called on any change to an input, which puts the last answer back behind the button. */
  onEdit?: () => void;
  /** A result is showing for these inputs (the button reads "Analyze again"). */
  ran?: boolean;
  /** The answer, for a step-by-step module that places it itself (`ToolInfo.wizard`). */
  result?: ReactNode;
};

function pick<T extends Record<string, unknown>>(base: T, saved?: Record<string, unknown>): T {
  if (!saved) return base;
  const out = { ...base };
  (Object.keys(base) as (keyof T)[]).forEach((k) => {
    const v = saved[k as string];
    if (v !== undefined && typeof v === typeof base[k]) out[k] = v as T[keyof T];
  });
  return out;
}

// ------------------------------------------------------------------ add an expense

type ExpenseForm = ExpenseValues;

export function ExpenseToolForm({ onRun, busy, defaults, onEdit, ran }: ToolFormProps) {
  const { today } = usePlanCtx();
  const { register, handleSubmit, watch, formState } = useForm<ExpenseForm>({
    resolver: zodResolver(expenseSchema),
    defaultValues: pick(expenseDefaults(today), defaults),
  });
  const e = formState.errors;
  useEdits(watch, onEdit);
  const recurring = watch("recurring");
  return (
    <form
      className="act-body dz-form"
      noValidate
      onSubmit={handleSubmit((v) => {
        const run = expenseRun(v, today);
        onRun(run.changes, run.inputs);
      })}
    >
      <Field label="What is it" error={e.name} htmlFor="ex-name">
        <TextInput id="ex-name" reg={register("name")} placeholder="e.g. Daycare" />
      </Field>
      <div className="two">
        <Field label="How much" error={e.amount} htmlFor="ex-amt">
          <MoneyInput id="ex-amt" reg={register("amount")} />
        </Field>
        <Field label={recurring ? "First payment" : "When"} error={e.dueDate} htmlFor="ex-due">
          <DateInput id="ex-due" reg={register("dueDate")} />
        </Field>
      </div>
      <label className="dz-check">
        <input type="checkbox" {...register("recurring")} /> This one repeats
      </label>
      {recurring && (
        <div className="three dz-repeat">
          <Field label="Every" error={e.period} htmlFor="ex-per">
            <TextInput id="ex-per" reg={register("period")} inputMode="numeric" />
          </Field>
          <Field label="Unit" htmlFor="ex-pt">
            <select id="ex-pt" {...register("periodType")}>
              <option value="weekly">Weeks</option>
              <option value="monthly">Months</option>
              <option value="yearly">Years</option>
            </select>
          </Field>
          <Field label="Until (optional)" error={e.endDate} htmlFor="ex-end">
            <DateInput id="ex-end" reg={register("endDate")} />
          </Field>
        </div>
      )}
      <RunButton busy={busy} ran={ran} />
    </form>
  );
}

// ------------------------------------------------------------------ buy a home

type HomeForm = HomeValues;

export function HomeToolForm({ onRun, busy, defaults, onEdit, ran }: ToolFormProps) {
  const { today } = usePlanCtx();
  const { register, handleSubmit, watch, formState } = useForm<HomeForm>({
    resolver: zodResolver(homeSchema),
    defaultValues: pick(homeDefaults(today), defaults),
  });
  const e = formState.errors;
  useEdits(watch, onEdit);
  const [price, dp, rate, years] = watch(["price", "downPayment", "rate", "amortizationYears"]);
  const principal = Math.max(0, amount(price) - amount(dp));
  const payment =
    principal > 0 && amount(years) > 0
      ? mortgagePayment(principal, amount(rate), amount(years))
      : 0;
  const dpShare = amount(price) > 0 ? amount(dp) / amount(price) : 0;
  return (
    <form
      className="act-body dz-form"
      noValidate
      onSubmit={handleSubmit((v) => {
        const run = homeRun(v, today);
        onRun(run.changes, run.inputs);
      })}
    >
      <Field label="Give it a name" error={e.name} htmlFor="hm-name">
        <TextInput id="hm-name" reg={register("name")} />
      </Field>
      <div className="two">
        <Field label="Home price" error={e.price} htmlFor="hm-price">
          <MoneyInput id="hm-price" reg={register("price")} />
        </Field>
        <Field
          label="Down payment"
          error={e.downPayment}
          htmlFor="hm-dp"
          hint={dpShare > 0 ? `${Math.round(dpShare * 100)}% of the price` : undefined}
        >
          <MoneyInput id="hm-dp" reg={register("downPayment")} />
        </Field>
      </div>
      <div className="three">
        <Field label="Mortgage rate (%)" error={e.rate} htmlFor="hm-rate">
          <TextInput id="hm-rate" reg={register("rate")} inputMode="decimal" />
        </Field>
        <Field label="Amortization (years)" error={e.amortizationYears} htmlFor="hm-am">
          <TextInput id="hm-am" reg={register("amortizationYears")} inputMode="numeric" />
        </Field>
        <Field label="When" error={e.purchaseDate} htmlFor="hm-when">
          <DateInput id="hm-when" reg={register("purchaseDate")} />
        </Field>
      </div>
      <div className="two">
        <Field label="Property tax, a year (optional)" error={e.annualPropertyTax} htmlFor="hm-tax">
          <MoneyInput id="hm-tax" reg={register("annualPropertyTax")} />
        </Field>
        <Field label="Home insurance, a year (optional)" error={e.annualInsurance} htmlFor="hm-ins">
          <MoneyInput id="hm-ins" reg={register("annualInsurance")} />
        </Field>
      </div>
      <div className="two">
        <Field label="Lender" htmlFor="hm-lender">
          <select id="hm-lender" {...register("lender")}>
            {LENDERS.map(([v, l]) => (
              <option key={v} value={String(v)}>
                {l}
              </option>
            ))}
          </select>
        </Field>
        <label className="dz-check" style={{ alignSelf: "center" }}>
          <input type="checkbox" {...register("primaryResidence")} /> We would live in it
        </label>
      </div>
      {payment > 0 && (
        <div className="dz-preview" data-testid="decision-preview">
          <span>Mortgage of {money(principal)}</span>
          <b>{money(payment)} a month</b>
          <span className="mut">Compounded semi-annually, as Canadian mortgages are.</span>
        </div>
      )}
      <RunButton busy={busy} ran={ran} />
    </form>
  );
}

// ------------------------------------------------------------------ buy a car

type CarForm = CarValues;

export function CarToolForm({ onRun, busy, defaults, onEdit, ran }: ToolFormProps) {
  const { today } = usePlanCtx();
  const { register, handleSubmit, watch, setValue, formState } = useForm<CarForm>({
    resolver: zodResolver(carSchema),
    defaultValues: pick(carDefaults(today), defaults),
  });
  const e = formState.errors;
  useEdits(watch, onEdit);
  const w = watch();
  const cash = w.how === "cash";
  const principal = cash ? 0 : Math.max(0, amount(w.price) - amount(w.downPayment));
  const cost = principal > 0 ? financingCost(principal, amount(w.apr), amount(w.termMonths)) : null;
  const fuel = fuelMonthly(amount(w.consumption), amount(w.distance), amount(w.fuelPrice));
  return (
    <form
      className="act-body dz-form"
      noValidate
      onSubmit={handleSubmit((v) => {
        const run = carRun(v, today);
        onRun(run.changes, run.inputs);
      })}
    >
      <Field label="Give it a name" error={e.name} htmlFor="car-name">
        <TextInput id="car-name" reg={register("name")} />
      </Field>
      <div className="field">
        <label id="car-how">How you would get it</label>
        <div className="dmseg" role="group" aria-labelledby="car-how">
          {(
            [
              ["finance", "Finance it"],
              ["cash", "Pay cash"],
            ] as const
          ).map(([v, l]) => (
            <button
              key={v}
              type="button"
              className={w.how === v ? "on" : ""}
              aria-pressed={w.how === v}
              onClick={() => setValue("how", v, { shouldDirty: true })}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className={cash ? "" : "two"}>
        <Field label="Car price" error={e.price} htmlFor="car-price">
          <MoneyInput id="car-price" reg={register("price")} />
        </Field>
        {!cash && (
          <Field label="Down payment or trade-in" error={e.downPayment} htmlFor="car-dp">
            <MoneyInput id="car-dp" reg={register("downPayment")} />
          </Field>
        )}
      </div>
      {cash ? (
        <Field label="When" error={e.purchaseDate} htmlFor="car-when">
          <DateInput id="car-when" reg={register("purchaseDate")} />
        </Field>
      ) : (
        <div className="three">
          <Field label="Rate (APR %)" error={e.apr} htmlFor="car-apr">
            <TextInput id="car-apr" reg={register("apr")} inputMode="decimal" />
          </Field>
          <Field label="Term (months)" error={e.termMonths} htmlFor="car-term">
            <TextInput id="car-term" reg={register("termMonths")} inputMode="numeric" />
          </Field>
          <Field label="When" error={e.purchaseDate} htmlFor="car-when">
            <DateInput id="car-when" reg={register("purchaseDate")} />
          </Field>
        </div>
      )}
      {cost && (
        <div className="dz-preview" data-testid="decision-preview">
          <span>Financing {money(principal)}</span>
          <b>{money(cost.payment)} a month</b>
          <span className="mut">{money(cost.interest)} in interest over the term.</span>
        </div>
      )}
      <h4 className="dz-sub">Running it</h4>
      <div className="two">
        <Field label="Insurance, a month" error={e.insuranceMonthly} htmlFor="car-ins">
          <MoneyInput id="car-ins" reg={register("insuranceMonthly")} />
        </Field>
        <Field label="Maintenance, a month" error={e.maintenanceMonthly} htmlFor="car-mnt">
          <MoneyInput id="car-mnt" reg={register("maintenanceMonthly")} />
        </Field>
      </div>
      <div className="three">
        <Field label="Fuel use (L/100 km)" error={e.consumption} htmlFor="car-l">
          <TextInput id="car-l" reg={register("consumption")} inputMode="decimal" />
        </Field>
        <Field label="Distance (km a month)" error={e.distance} htmlFor="car-km">
          <TextInput id="car-km" reg={register("distance")} inputMode="decimal" />
        </Field>
        <Field label="Fuel price ($/L)" error={e.fuelPrice} htmlFor="car-fp">
          <TextInput id="car-fp" reg={register("fuelPrice")} inputMode="decimal" />
        </Field>
      </div>
      {fuel > 0 && <p className="hint">About {money(fuel)} a month in fuel.</p>}
      <RunButton busy={busy} ran={ran} />
    </form>
  );
}

// ------------------------------------------------------------------ finance something

type FinancedForm = FinancedValues;

export function FinancedToolForm({ onRun, busy, defaults, onEdit, ran }: ToolFormProps) {
  const { today } = usePlanCtx();
  const { register, handleSubmit, watch, formState } = useForm<FinancedForm>({
    resolver: zodResolver(financedSchema),
    defaultValues: pick(financedDefaults(today), defaults),
  });
  const e = formState.errors;
  useEdits(watch, onEdit);
  const w = watch();
  const principal = Math.max(0, amount(w.total) - amount(w.downPayment));
  const cost = principal > 0 ? financingCost(principal, amount(w.apr), amount(w.termMonths)) : null;
  return (
    <form
      className="act-body dz-form"
      noValidate
      onSubmit={handleSubmit((v) => {
        const run = financedRun(v, today);
        onRun(run.changes, run.inputs);
      })}
    >
      <Field label="What is it" error={e.name} htmlFor="fn-name">
        <TextInput id="fn-name" reg={register("name")} placeholder="e.g. A new roof" />
      </Field>
      <div className="two">
        <Field label="Total cost" error={e.total} htmlFor="fn-total">
          <MoneyInput id="fn-total" reg={register("total")} />
        </Field>
        <Field label="Paid up front" error={e.downPayment} htmlFor="fn-dp">
          <MoneyInput id="fn-dp" reg={register("downPayment")} />
        </Field>
      </div>
      <div className="three">
        <Field label="Rate (APR %)" error={e.apr} htmlFor="fn-apr">
          <TextInput id="fn-apr" reg={register("apr")} inputMode="decimal" />
        </Field>
        <Field label="Term (months)" error={e.termMonths} htmlFor="fn-term">
          <TextInput id="fn-term" reg={register("termMonths")} inputMode="numeric" />
        </Field>
        <Field label="When" error={e.purchaseDate} htmlFor="fn-when">
          <DateInput id="fn-when" reg={register("purchaseDate")} />
        </Field>
      </div>
      <Field label="Kind of financing" htmlFor="fn-type">
        <select id="fn-type" {...register("loanType")}>
          {LOAN_TYPES.map(([v, l]) => (
            <option key={v} value={String(v)}>
              {l}
            </option>
          ))}
        </select>
      </Field>
      {cost && (
        <div className="dz-preview" data-testid="decision-preview">
          <span>Financing {money(principal)}</span>
          <b>{money(cost.payment)} a month</b>
          <span className="mut">
            {money(cost.interest)} in interest, so it costs {money(amount(w.total) + cost.interest)}{" "}
            in all instead of {money(amount(w.total))} paid in cash.
          </span>
        </div>
      )}
      <RunButton busy={busy} ran={ran} />
    </form>
  );
}
